/**
 * Per-route SEO head models (#241, FR-15.4/15.5) — the single source of truth for EVERY
 * route's `<head>`, consumed by both the client hook ({@link useDocumentHead}, applied in
 * each page) and the build-time multi-route prerender ({@link ../prerender-entry}). One
 * definition per route means the crawled HTML and the hydrated app can never drift, and
 * every indexable route ships a **self-referencing** canonical (the #241 fix for the bug
 * where `/host`, `/player`, … inherited the landing shell's `canonical=/`).
 *
 * Titles/descriptions derive from the keyword anchor (`docs/SEO_KEYWORDS.md`, D241.9).
 * Brand strings read from {@link BRAND_NAME} (FR-9.11) — never hard-coded. Pure + DOM-free
 * so it unit-tests directly and runs under Node SSR.
 */

import {
  TRIVIA_BOARDS,
  triviaPageBySlug,
  triviaPagesForBoards,
  type TriviaPage,
} from '@jeopardy/shared';
import { BRAND_NAME } from '../ui/brand';
import { OG_IMAGE_PATH, PROD_ORIGIN, type DocumentHead } from './head-tags';
import { LANDING_SEO } from './landing-seo';
import { AFFILIATION_ANSWER } from './trademark';
import { SEO_LANDING_HEADS } from './seo-landings';

/** Host-surface head (#241, D241.5) — host intent, self-canonical, indexable. */
export const HOST_SEO: DocumentHead = {
  title: `Host a trivia game · ${BRAND_NAME}`,
  description: `Host a live trivia game online with ${BRAND_NAME}: pick your topics, get an AI-generated board on demand, share a link, and run the buzzer from your laptop.`,
  canonicalPath: '/host',
  image: OG_IMAGE_PATH,
};

/** Player-surface head (#241, D241.5) — join intent, self-canonical, indexable. */
export const PLAYER_SEO: DocumentHead = {
  title: `Join a trivia game · ${BRAND_NAME}`,
  description: `Join a live ${BRAND_NAME} trivia game with a code — no download, play from any phone. Race the buzzer and battle your friends in real time.`,
  canonicalPath: '/player',
  image: OG_IMAGE_PATH,
};

/**
 * Leaderboard head (#285, D-285.5) — self-canonical but `noindex`: the global board is now
 * login-gated (#285 reverses the #103/#241 D241.3 indexable-landing decision), so it is an
 * app-only surface, not a search landing. Client-only (dropped from the prerender set +
 * sitemap; robots-`Disallow`ed via {@link ./crawler-assets}.DISALLOWED_PATHS).
 */
export const LEADERBOARD_SEO: DocumentHead = {
  title: `Leaderboard · ${BRAND_NAME}`,
  description: `The ${BRAND_NAME} leaderboard — top players and recent game standings.`,
  canonicalPath: '/leaderboard',
  robots: 'noindex',
};

/** FAQ content page (#241, D241.6) — question intent, indexable; FAQPage JSON-LD below. */
export const FAQ_SEO: DocumentHead = {
  title: `${BRAND_NAME} FAQ — AI trivia generator & buzzer game`,
  description: `Answers to common questions about ${BRAND_NAME} — the AI trivia generator, live buzzer party play, custom quiz topics, and virtual team trivia.`,
  canonicalPath: '/faq',
  image: OG_IMAGE_PATH,
  // jsonLd filled below from FAQ_ITEMS so the visible Q&A and the FAQPage structured
  // data derive from ONE source (Google requires the markup to match the on-page text).
};

/** Terms-of-service head (#126/#241) — indexable, self-canonical. */
export const TERMS_SEO: DocumentHead = {
  title: `Terms of Service · ${BRAND_NAME}`,
  description: `The Terms of Service for ${BRAND_NAME}, the live multiplayer trivia game operated by Nance Technologies LLC.`,
  canonicalPath: '/terms',
  image: OG_IMAGE_PATH,
};

/** Privacy-policy head (#126/#241) — indexable, self-canonical. */
export const PRIVACY_SEO: DocumentHead = {
  title: `Privacy Policy · ${BRAND_NAME}`,
  description: `How ${BRAND_NAME} collects, uses, and protects your information — the privacy notice for the live multiplayer trivia game.`,
  canonicalPath: '/privacy',
  image: OG_IMAGE_PATH,
};

/** Refund-policy head (#126/#241) — indexable, self-canonical. */
export const REFUNDS_SEO: DocumentHead = {
  title: `Refund & Cancellation Policy · ${BRAND_NAME}`,
  description: `The refund and cancellation policy for ${BRAND_NAME} credits — a prepaid digital good.`,
  canonicalPath: '/refunds',
  image: OG_IMAGE_PATH,
};

/**
 * Profile head (#241, D241.5) — self-canonical but `noindex`: an auth-gated app surface,
 * not a search landing. Client-only (not prerendered).
 */
export const PROFILE_SEO: DocumentHead = {
  title: `Profile · ${BRAND_NAME}`,
  description: `Your ${BRAND_NAME} profile — account, credits, and preferences.`,
  canonicalPath: '/profile',
  robots: 'noindex',
};

/**
 * Presentation head (#241, D241.5) — self-canonical but `noindex`: the shared-screen TV
 * surface for an in-progress game, not a search landing. Client-only (not prerendered).
 */
export const PRESENTATION_SEO: DocumentHead = {
  title: `Presentation · ${BRAND_NAME}`,
  description: `The ${BRAND_NAME} shared-screen presentation view — cast a live game to the TV.`,
  canonicalPath: '/presentation',
  robots: 'noindex',
};

/**
 * The FAQ content (#241, D241.6) — one source for both the rendered {@link ../pages/FaqPage}
 * and the `FAQPage` JSON-LD. Trademark-safe copy targeting the keyword anchor's terms.
 */
export const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: `What is ${BRAND_NAME}?`,
    a: `${BRAND_NAME} is a live, multiplayer trivia buzzer game. You pick the topics, ${BRAND_NAME} generates a full board of trivia on demand with AI, you share a link, and everyone races the buzzer in real time — on the couch or across the world.`,
  },
  {
    q: `Does ${BRAND_NAME} really generate the trivia questions with AI?`,
    a: `Yes. Every board is generated on demand around the topics you choose — '90s movies, world capitals, your team's inside jokes — so no two games are the same. There are no stale, pre-written question packs.`,
  },
  {
    q: `How do players join a game?`,
    a: `Players join from any phone or browser with a short code or a QR link — no app download and no account needed to play. The host runs the game from a laptop and can cast the board to a TV for the full game-show feel.`,
  },
  {
    q: `Can I use ${BRAND_NAME} for remote or virtual team trivia?`,
    a: `Absolutely. Because everyone joins by link and buzzes in from their own device, ${BRAND_NAME} works just as well for a remote team spread across time zones as it does for a party in one room.`,
  },
  {
    q: `Is ${BRAND_NAME} free?`,
    a: `${BRAND_NAME} is free to try. Hosting AI-generated games uses credits; you can start without a download and see how it plays before you buy anything.`,
  },
  {
    q: `How many people can play?`,
    a: `As many as your group needs — there's no fixed cap, so bring the whole room. ${BRAND_NAME} is built for a party: one host, a shared screen everyone can watch, and each player buzzing in from their own phone, whether that's a few friends or a big group.`,
  },
  // #353: Jeopardy-relevance questions. "Jeopardy-style" is descriptive nominative use
  // permitted by FR-9.11 (D353.1); the affiliation clarification below is required.
  {
    q: `Can I create my own Jeopardy-style game?`,
    a: `Yes — that's the whole idea. Give ${BRAND_NAME} the topics you want and it generates a complete, playable Jeopardy-style board of categories and clues on demand. You can review and tweak anything before you start, then share a link and play.`,
  },
  {
    q: `Can I choose my own topics?`,
    a: `Always. You pick the topics for every game — a school subject, a movie franchise, your family history, your team's inside jokes — and ${BRAND_NAME} builds the board around them. Nothing is pre-canned.`,
  },
  {
    q: `Does ${BRAND_NAME} have a buzzer?`,
    a: `Yes. Every player's phone is a live buzzer: when buzzing opens, the first player to buzz in gets to answer, in real time, just like a game show — no extra hardware and no app to install.`,
  },
  {
    q: `Is ${BRAND_NAME} affiliated with Jeopardy?`,
    a: AFFILIATION_ANSWER,
  },
];

/** Build the `FAQPage` JSON-LD (#241, D241.7) from {@link FAQ_ITEMS}. */
export function faqJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

FAQ_SEO.jsonLd = faqJsonLd();

/**
 * Trivia index head (#295) — the `/trivia` hub of curated, playable trivia sets.
 * Indexable, self-canonical; targets the "free trivia questions and answers" hub intent.
 * The `BreadcrumbList` + `ItemList` JSON-LD is attached below once the page list is known.
 */
export const TRIVIA_INDEX_SEO: DocumentHead = {
  title: `Free Trivia Questions & Answers to Play · ${BRAND_NAME}`,
  description: `Free trivia questions and answers by theme — history, science, 80s movies, Christmas and more. Reveal the answers, then play the board live with phones as buzzers.`,
  canonicalPath: '/trivia',
  image: OG_IMAGE_PATH,
};

/**
 * Absolute URL for a `/trivia/<slug>` (and the hub/home) — breadcrumb `item` values
 * must be absolute on the canonical production host.
 */
function triviaUrl(path: string): string {
  return `${PROD_ORIGIN}${path}`;
}

/**
 * Structured data for a trivia page (#295): a `Quiz` plus a `BreadcrumbList`, emitted as a
 * JSON-LD array in one `<script>`.
 *
 * Type choice — `Quiz`, NOT `FAQPage`: the content is a set of reveal-able trivia
 * questions, i.e. an educational quiz, not "frequently asked questions". Google's FAQ rich
 * result was deprecated for non-authoritative sites (Aug 2023), so `FAQPage` here buys no
 * eligibility and misdescribes the content. `Quiz` with `Question`/`eduQuestionType:
 * 'Flashcard'` children is the semantically correct type for a reveal-able Q&A set and maps
 * to Google's education "practice problems / flashcard" structured data. `QAPage` is wrong
 * (it models ONE community-answered question per page). Each `Question`'s prompt + answer is
 * in the crawlable on-page DOM (the `<details>` rows), so the markup matches the visible
 * text — the same discipline as {@link faqJsonLd}. The `BreadcrumbList` (Home › Trivia ›
 * topic) mirrors the on-page breadcrumb link and is well-supported for breadcrumb rich
 * results.
 */
export function triviaJsonLd(page: TriviaPage): Record<string, unknown>[] {
  const quiz: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Quiz',
    name: page.title,
    about: { '@type': 'Thing', name: page.topic },
    hasPart: page.items.map((item) => ({
      '@type': 'Question',
      eduQuestionType: 'Flashcard',
      name: item.prompt,
      acceptedAnswer: { '@type': 'Answer', text: item.response },
    })),
  };
  const breadcrumb: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: triviaUrl('/') },
      { '@type': 'ListItem', position: 2, name: 'Trivia', item: triviaUrl('/trivia') },
      {
        '@type': 'ListItem',
        position: 3,
        name: page.title,
        item: triviaUrl(`/trivia/${page.slug}`),
      },
    ],
  };
  return [quiz, breadcrumb];
}

/**
 * The `DocumentHead` for a published `/trivia/<slug>` page (#295), or `undefined` when
 * the slug isn't a published trivia page. Unique title/description/canonical + the Q&A
 * `FAQPage` JSON-LD, all derived from the committed board so the crawled HTML and the
 * hydrated app can't drift.
 */
export function triviaHeadFor(slug: string): DocumentHead | undefined {
  const page = triviaPageBySlug(TRIVIA_BOARDS, slug);
  if (!page) return undefined;
  return {
    // Brand suffix appended here (from BRAND_NAME) — `seoTitle` stays brand-token-free in
    // `shared`; the web SEO layer owns the wordmark.
    title: `${page.seoTitle} · ${BRAND_NAME}`,
    description: page.seoDescription,
    canonicalPath: `/trivia/${page.slug}`,
    image: OG_IMAGE_PATH,
    jsonLd: triviaJsonLd(page),
  };
}

/**
 * Structured data for the `/trivia` hub (#295): a `BreadcrumbList` (Home › Trivia) plus an
 * `ItemList` of the published topic pages, so crawlers see the hub → page relationships and
 * the collection is eligible for a carousel-style result. The `ItemList` is omitted for the
 * empty baseline (no published boards) so we never emit an empty list.
 */
export function triviaIndexJsonLd(): Record<string, unknown>[] {
  const breadcrumb: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: triviaUrl('/') },
      { '@type': 'ListItem', position: 2, name: 'Trivia', item: triviaUrl('/trivia') },
    ],
  };
  const pages = triviaPagesForBoards(TRIVIA_BOARDS);
  if (pages.length === 0) return [breadcrumb];
  const itemList: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: pages.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.title,
      url: triviaUrl(`/trivia/${p.slug}`),
    })),
  };
  return [breadcrumb, itemList];
}

TRIVIA_INDEX_SEO.jsonLd = triviaIndexJsonLd();

/**
 * Head models for the published trivia pages, keyed by `/trivia/<slug>` (#295). Computed
 * once from {@link TRIVIA_BOARDS} at module load — an empty board set yields an empty map,
 * so the baseline (no committed boards) is legal.
 */
const TRIVIA_PAGE_HEADS: Record<string, DocumentHead> = Object.fromEntries(
  triviaPagesForBoards(TRIVIA_BOARDS).map((p) => [
    `/trivia/${p.slug}`,
    triviaHeadFor(p.slug)!,
  ]),
);

/**
 * Head models for every route the client sets a head on, keyed by path. The indexable
 * subset ({@link ./crawler-assets}.PRERENDER_PATHS) is baked at prerender time; the
 * `noindex` app routes are here for the client hook only.
 */
export const ROUTE_HEADS: Record<string, DocumentHead> = {
  '/': LANDING_SEO,
  '/host': HOST_SEO,
  '/player': PLAYER_SEO,
  '/leaderboard': LEADERBOARD_SEO,
  '/faq': FAQ_SEO,
  '/terms': TERMS_SEO,
  '/privacy': PRIVACY_SEO,
  '/refunds': REFUNDS_SEO,
  '/profile': PROFILE_SEO,
  '/presentation': PRESENTATION_SEO,
  '/trivia': TRIVIA_INDEX_SEO,
  ...TRIVIA_PAGE_HEADS,
  // #353 (FR-15.7): the four product-intent Jeopardy-style landing pages.
  ...SEO_LANDING_HEADS,
};

/**
 * Resolve a route path to its head model (throws for an unmapped path — a build bug).
 * `/trivia/<slug>` pages are matched by prefix so a published slug always resolves even
 * if it wasn't pre-baked into {@link ROUTE_HEADS} at module load.
 */
export function headModelFor(path: string): DocumentHead {
  const head = ROUTE_HEADS[path];
  if (head) return head;
  if (path.startsWith('/trivia/')) {
    const triviaHead = triviaHeadFor(path.slice('/trivia/'.length));
    if (triviaHead) return triviaHead;
  }
  throw new Error(`route-seo: no head model for "${path}"`);
}
